import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CampaignStatus } from '../../core/enum/campaign-status.enum';
import { Role } from '../../core/enum/role.enum';
import { AttachmentType } from '../../core/models/attachment/attachment-type';
import { AttachmentAudience } from '../../core/enum/attachment-audience.enum';
import { AccountService } from '../../core/services/api/account.service';
import { BusinessesService } from '../../core/services/api/mysec/businesses.service';
import { SettingService } from '../../core/services/api/setting.service';
import { ErrorService } from '../../core/services/error.service';
import { StorageService } from '../../core/services/storage.service';
import { ToastService } from '../../core/services/toast.service';
import { BlockUiService } from '../../shared/services/blockUi/block-ui.service';
import { GeneralDocumentUploadComponent } from '../../pages/mysec/mysec-settings/team-management/ticker-code/general-document-upload/general-document-upload.component';
import { PostNewAnnouncementComponent } from '../../pages/mysec/mysec-settings/team-management/ticker-code/post-new-announcement/post-new-announcement.component';

/**
 * `navigate` = in-app route (`route`); `external` = new tab (`externalUrl`).
 * `external` rather than `clickup`: the two form cards happen to point at ClickUp
 * today, but that's the ops team's hosting choice — if a form moves in-house it
 * should be a URL swap, not a new action type.
 */
type NudgeAction = 'announce' | 'upload' | 'navigate' | 'external' | 'none';

/** A single Smart Suggestion. `key` is its stable identity for `@for` tracking. */
export interface IssuerNudge {
  key: string;
  icon: 'megaphone' | 'upload' | 'chart' | 'trend' | 'vote' | 'gift' | 'raise';
  iconWrap: string; // tailwind classes for the icon chip
  kicker: string; // i18n key — the card's visible heading
  cta: string; // i18n key (primary button)
  action: NudgeAction;
  route?: string; // in-app route, for action: 'navigate'
  externalUrl?: string; // absolute URL opened in a new tab, for action: 'external'
  secondaryCta?: string; // i18n key (text link under the CTA)
  priority: number; // lower sorts first (fixed order, not a queue)
  teaser?: boolean; // "coming soon" — rendered muted, never counted in the badge
}

/**
 * FRC-686 — Next steps panel.
 *
 * Floating, minimizable, non-blocking dock that follows the issuer on EVERY page
 * (mounted once in app.component.html; there is no dedicated issuer shell). It surfaces
 * up to six Smart Suggestion cards. Card #6 (dividend) is a "coming soon" teaser: shown
 * muted with a SOON pill and a disabled button, and never counted in the badge.
 */
@Component({
  selector: 'app-issuer-next-steps-panel',
  templateUrl: './issuer-next-steps-panel.component.html',
  styleUrls: ['./issuer-next-steps-panel.component.scss'],
  standalone: false,
})
export class IssuerNextStepsPanelComponent implements OnInit, AfterViewChecked, OnDestroy {
  /** Card 4 "Express interest (PSTX)" — ops-hosted ClickUp intake form (FRC-686). */
  private static readonly PSTX_INTEREST_FORM_URL =
    'https://forms.clickup.com/9008241541/f/8cexpw5-92076/PHXDGHW9J5MRHP1BDJ';
  private static readonly SHAREHOLDERS_ROUTE = '/postfundraising/shareholders';
  /**
   * Card 3 "Check cap table" — the Shareholders Dashboard *is* the cap table /
   * shareholder register surface per the ticket, so this deliberately shares
   * SHAREHOLDERS_ROUTE rather than pointing at a page of its own.
   */
  private static readonly CAP_TABLE_ROUTE = IssuerNextStepsPanelComponent.SHAREHOLDERS_ROUTE;
  /**
   * Card 6 "Raise your next round" — the ECF application form. The ticket cites
   * this as `staging.pitchin.dev/raise/raise-fund-form`; that's just the staging
   * host of this same app, so it's an in-app route, not an external link.
   */
  private static readonly RAISE_FUND_FORM_ROUTE = '/raise/raise-fund-form';
  /**
   * Card 5 "Propose a Share Buyback" — ops-hosted ClickUp intake form (FRC-686).
   *
   * Not the in-app `pstx/corporate-action/:id` route: that page renders one
   * *existing* corporate action and needs an id, whereas this card is the entry
   * point for proposing a new one. The ticket names this form as the target.
   */
  private static readonly CORPORATE_ACTION_FORM_URL =
    'https://forms.clickup.com/9008241541/f/8cexpw5-103156/EA5EOMD7KVQARSEPH0';
  /**
   * Body class toggled while this panel is EXPANDED (not just present). The
   * sheet/card and the site's scroll-to-top FAB both want the fixed
   * bottom-right corner — rather than permanently reserve clearance for the
   * FAB (which stops the sheet sitting flush to the bottom) or permanently
   * hide it (which loses the feature outright), this follows the Maps/
   * fintech bottom-sheet convention: secondary FABs fade out only while the
   * sheet is actively expanded, and fade back in the instant it's minimized
   * to its collapsed pill/handle. See the fade transition in src/styles.scss.
   */
  private static readonly BODY_EXPANDED_CLASS = 'ns-panel-expanded';

  /**
   * Height this dock occupies along the bottom edge, published on :root so page-level
   * bottom bars can sit above it instead of behind. The campaign summary's sticky CTA
   * bar consumes this; it is the mirror of the --mobile-bottom-bar-height that bar
   * publishes for the site's scroll-to-top FAB.
   */
  private static readonly DOCK_HEIGHT_VAR = '--next-steps-dock-height';
  /** Matches the SCSS mobile-bottom-sheet cutoff (see the component's .scss). */
  private static readonly MOBILE_BREAKPOINT_PX = 768;
  /** 30 MB — the ticket's stated upload ceiling, same value the dashboard passes. */
  private static readonly MAX_UPLOAD_BYTES = 31457280;
  /**
   * Page size for the eligibility lookup. Deliberately large to take pagination out
   * of the picture in one request — the same trick ticker-code.component.ts uses on
   * its own list ("set this `take` to bigger value to `disable` pagination"). An
   * issuer with more campaigns than this would fail *closed* (panel hidden), which
   * is the safe direction, but it's well beyond any realistic campaign count.
   */
  private static readonly CAMPAIGN_LOOKUP_TAKE = 100;

  minimized = false;

  /** Mobile carousel: index of the card currently snapped into view (for the dots). */
  @ViewChild('nsBody') private nsBody?: ElementRef<HTMLElement>;
  @ViewChild('nsRoot') private nsRoot?: ElementRef<HTMLElement>;
  activeSlide = 0;

  /**
   * Whether this issuer has at least one campaign that reached `Success`.
   *
   * Starts `false` so the panel is hidden until the check positively confirms
   * eligibility — it must never flash into view and then vanish, and an ineligible
   * issuer should see "no trace" of it at any point.
   */
  private hasSuccessfulCampaign = false;

  /**
   * `role:userId` the current verdict belongs to, so the check runs once per identity
   * rather than once per navigation. Undefined until the first check.
   */
  private eligibilityCheckedFor?: string;

  /** Router-events subscription, released in ngOnDestroy. */
  private readonly subscriptions = new Subscription();

  /**
   * Every request whose result belongs to *one specific* signed-in identity.
   *
   * Replaced wholesale whenever the identity changes, which cancels anything still
   * in flight. Without this, a slow eligibility or company lookup started as issuer A
   * could resolve after B signed in and apply A's verdict — or worse, A's companyId —
   * to B. Unsubscribing an HttpClient subscription aborts the request, so the late
   * response never lands at all rather than being filtered out on arrival.
   */
  private identityRequests = new Subscription();

  /** Issuer's company, resolved once in `ngOnInit`. Undefined until then, or on lookup failure. */
  private companyId?: number;

  /** Attachment types for the upload POST, resolved once in `ngOnInit`. */
  private attachmentTypes: AttachmentType[] = [];

  private readonly nudges: IssuerNudge[] = [
    {
      key: 'announcement',
      icon: 'megaphone',
      iconWrap: 'tw-bg-primary-light tw-text-primary',
      kicker: 'nextSteps.announcement.kicker',
      cta: 'nextSteps.announcement.cta',
      action: 'announce',
      priority: 1,
    },
    {
      key: 'document',
      icon: 'upload',
      iconWrap: 'tw-bg-warning-light tw-text-warning',
      kicker: 'nextSteps.document.kicker',
      cta: 'nextSteps.document.cta',
      action: 'upload',
      priority: 2,
    },
    {
      key: 'capTable',
      icon: 'chart',
      iconWrap: 'tw-bg-ecf-light tw-text-link',
      kicker: 'nextSteps.capTable.kicker',
      cta: 'nextSteps.capTable.cta',
      action: 'navigate',
      route: IssuerNextStepsPanelComponent.CAP_TABLE_ROUTE,
      priority: 3,
    },
    {
      key: 'pstx',
      icon: 'trend',
      iconWrap: 'tw-bg-mysec-light tw-text-pstx',
      kicker: 'nextSteps.pstx.kicker',
      cta: 'nextSteps.pstx.cta',
      action: 'external',
      externalUrl: IssuerNextStepsPanelComponent.PSTX_INTEREST_FORM_URL,
      // No `route`: the ticket struck through the secondary "View on Shareholders
      // Dashboard" text link, and nothing in the template renders one.
      priority: 4,
    },
    {
      key: 'voting',
      icon: 'vote',
      iconWrap: 'tw-bg-private-lightest tw-text-private',
      kicker: 'nextSteps.voting.kicker',
      cta: 'nextSteps.voting.cta',
      action: 'external',
      externalUrl: IssuerNextStepsPanelComponent.CORPORATE_ACTION_FORM_URL,
      priority: 5,
    },
    {
      key: 'newRound',
      icon: 'raise',
      iconWrap: 'tw-bg-warning-light tw-text-warning',
      kicker: 'nextSteps.newRound.kicker',
      cta: 'nextSteps.newRound.cta',
      action: 'navigate',
      route: IssuerNextStepsPanelComponent.RAISE_FUND_FORM_ROUTE,
      priority: 6,
    },
    {
      key: 'dividend',
      icon: 'gift',
      iconWrap: 'tw-bg-gray-100 tw-text-gray-400',
      kicker: 'nextSteps.dividend.kicker',
      cta: 'nextSteps.comingSoon',
      action: 'none',
      priority: 7,
      teaser: true,
    },
  ];

  constructor(
    @Inject(PLATFORM_ID) private readonly platform: Object,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly modalService: NgbModal,
    private readonly accountService: AccountService,
    private readonly settingService: SettingService,
    private readonly businessesService: BusinessesService,
    private readonly errorService: ErrorService,
    private readonly blockUiService: BlockUiService,
  ) {}

  ngOnInit(): void {
    // TODO(FRC-686): derive which nudges are valid from real company/counter/activity
    // state once the evaluation service / API lands. For now all six are shown.
    this.syncEligibility();

    // Logging in/out can happen via SPA navigation with no full reload, so ngOnInit
    // alone would leave the previous visitor's verdict in place — a second issuer
    // signing in without a reload could inherit the first one's panel. Re-checking on
    // navigation fixes that; `syncEligibility` is identity-guarded, so this costs a
    // string compare per navigation and an HTTP call only when the user changes.
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.syncEligibility()),
    );
  }

  /**
   * Decide eligibility for whoever is signed in now, at most once per identity.
   *
   * The ticket wants this checked "once per session, not on every page". Keying on
   * role + userId gives exactly that: navigation re-enters this method constantly but
   * returns immediately unless the signed-in identity actually changed.
   *
   * Deliberately NOT cached in session/localStorage — a campaign that turns Success
   * mid-session should begin showing the panel, and a cached failure would keep it
   * suppressed until storage was cleared.
   */
  private syncEligibility(): void {
    if (!isPlatformBrowser(this.platform)) return;

    const identity = `${this.storageService.role}:${this.storageService.userId}`;
    if (identity === this.eligibilityCheckedFor) return;
    this.eligibilityCheckedFor = identity;

    // Cancel anything still in flight for the identity we're leaving, so a late
    // response can't apply the previous user's data to this one.
    this.identityRequests.unsubscribe();
    this.identityRequests = new Subscription();

    // Drop the previous identity's state before re-deciding, so a logout can never
    // leave a stale panel (or another company's id) behind.
    this.hasSuccessfulCampaign = false;
    this.companyId = undefined;
    this.attachmentTypes = [];

    // Role-gated because the component instantiates for *every* visitor (the template
    // is what `shouldShow` gates, not the component) — without this, every investor
    // and guest would fire an issuer campaign lookup that isn't theirs.
    if (this.storageService.role !== Role.IssuerRep) return;
    this.fetchEligibility();
  }

  /**
   * The panel is for issuers who have actually *completed* a raise — a company
   * mid-raise, in cooling-off, or awaiting share allotment must see nothing at all.
   * So eligibility is "at least one campaign that reached `Success`".
   */
  private fetchEligibility(): void {
    this.identityRequests.add(
      this.accountService
        .getMyCampaigns(1, IssuerNextStepsPanelComponent.CAMPAIGN_LOOKUP_TAKE)
        .subscribe({
          next: (result) => {
            this.hasSuccessfulCampaign = (result?.data || []).some(
              (campaign) => campaign.campaignStatus === CampaignStatus.Success,
            );
            // Only now worth paying for the data the cards need.
            if (this.hasSuccessfulCampaign) this.loadCompanyContext();
          },
          // Silent by design: the ticket requires a failed check to simply leave the
          // panel absent — no error message, nothing else on the page affected.
          error: () => (this.hasSuccessfulCampaign = false),
        }),
    );
  }

  /**
   * Supporting data for cards 1 & 2 (announcement is company-scoped; upload needs an
   * attachment type). Fetched up front so those CTAs open without a stall, and only
   * once eligibility passed — an ineligible issuer costs one request, not three.
   */
  private loadCompanyContext(): void {
    this.identityRequests.add(
      this.accountService.getCompanyInfo().subscribe({
        next: (company) => (this.companyId = company?.id),
        // Deliberately silent: a failed lookup must not put an error on an unrelated
        // page. `companyId` stays undefined and the CTA explains itself if clicked.
        error: () => (this.companyId = undefined),
      }),
    );

    this.identityRequests.add(
      this.settingService.getMysecAttachmentTypes().subscribe({
        next: (types) => (this.attachmentTypes = types || []),
        error: () => (this.attachmentTypes = []), // silent, as above
      }),
    );
  }

  // `shouldShow`/`minimized` can change without this component's own inputs
  // changing (e.g. login via SPA navigation, no full reload). Re-syncing every
  // checked cycle (cheap — a single idempotent classList.toggle) is simpler
  // and more robust than trying to enumerate every event that could flip them.
  ngAfterViewChecked(): void {
    this.syncFabFade();
    this.syncDockHeight();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.identityRequests.unsubscribe();
    if (isPlatformBrowser(this.platform)) {
      document.body.classList.remove(IssuerNextStepsPanelComponent.BODY_EXPANDED_CLASS);
      document.documentElement.style.removeProperty(IssuerNextStepsPanelComponent.DOCK_HEIGHT_VAR);
    }
  }

  /**
   * Publish how much of the bottom edge this dock takes up, in both states — a page
   * bottom bar should rest on top of the collapsed pill and of the expanded sheet
   * alike, never behind either.
   *
   * Safe to report the expanded height: .ns-shell is capped at min(62dvh, 500px), so
   * what is left over always has room for a bar above it.
   *
   * Differs deliberately from syncFabFade, which hides the scroll-to-top FAB while the
   * sheet is open rather than moving it — the FAB is incidental, these CTAs are not.
   */
  private syncDockHeight(): void {
    if (!isPlatformBrowser(this.platform)) return;
    const el = this.nsRoot?.nativeElement;
    // Distance from the viewport bottom to the dock's TOP, not merely its height: the
    // value is consumed as a `bottom` inset, and on desktop the panel is inset from the
    // bottom edge (tw-bottom-6). offsetHeight alone would let a consumer overlap the
    // dock by that offset. Equal to offsetHeight on phones, where it docks flush at 0.
    const height = this.shouldShow && el
      ? Math.max(0, window.innerHeight - el.getBoundingClientRect().top)
      : 0;
    document.documentElement.style.setProperty(
      IssuerNextStepsPanelComponent.DOCK_HEIGHT_VAR,
      height + 'px',
    );
  }

  /** Fade the site's scroll-to-top FAB out while the sheet is expanded, back in once it's collapsed. */
  private syncFabFade(): void {
    if (!isPlatformBrowser(this.platform)) return;
    const expanded = this.shouldShow && !this.minimized;
    document.body.classList.toggle(IssuerNextStepsPanelComponent.BODY_EXPANDED_CLASS, expanded);
  }

  /**
   * Panel follows the issuer everywhere, but only for an issuer rep whose company
   * has completed a raise.
   *
   * The role check alone also covers the "no panel" cases for investors, admins and
   * guests: `storageService.role` is null when logged out, and every other role
   * fails the equality check. `hasSuccessfulCampaign` is what additionally excludes
   * an issuer who is still live / cooling-off / awaiting share allotment, or whose
   * campaigns all failed.
   */
  get shouldShow(): boolean {
    return (
      isPlatformBrowser(this.platform) &&
      this.storageService.role === Role.IssuerRep &&
      this.hasSuccessfulCampaign &&
      this.visibleNudges.length > 0
    );
  }

  /**
   * All cards, in fixed order. Every card is always present — the ticket is explicit
   * that there is no per-card close control and that a card survives being acted on
   * ("Post an announcement successfully → the announcement card is still there").
   * `sort` is non-mutating on a copy so the source array keeps its declared order.
   */
  get visibleNudges(): IssuerNudge[] {
    return [...this.nudges].sort((a, b) => a.priority - b.priority);
  }

  get actionableNudges(): IssuerNudge[] {
    return this.visibleNudges.filter((n) => !n.teaser);
  }

  get teaserNudges(): IssuerNudge[] {
    return this.visibleNudges.filter((n) => n.teaser);
  }

  /** Count badge — actionable cards only, never the coming-soon teaser. */
  get badgeCount(): number {
    return this.actionableNudges.length;
  }

  toggleMinimized(): void {
    this.minimized = !this.minimized;
  }

  /** On mobile the card stack is a horizontal snap-carousel — track which card is centered. */
  onBodyScroll(): void {
    const el = this.nsBody?.nativeElement;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll<HTMLElement>('.ns-card'));
    if (!cards.length) return;
    const center = el.getBoundingClientRect().left + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    cards.forEach((c, i) => {
      const r = c.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    this.activeSlide = best;
  }

  /** Pagination dot clicked — smooth-scroll the carousel to that card and reflect it immediately. */
  goToSlide(index: number): void {
    const el = this.nsBody?.nativeElement;
    if (!el) return;
    const target = el.querySelectorAll<HTMLElement>('.ns-card')[index];
    if (!target) return;
    this.activeSlide = index; // instant dot feedback — don't wait on the scroll event
    target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  // ---- Mobile-only dot "scrubbing": press-and-drag across the dots to scan
  // through cards, instead of one tap per dot. Bound on the .ns-dots container
  // (not per-dot) since a drag needs to track whichever dot is currently under
  // the finger, not just the one it started on. Gated on viewport width so a
  // touch-capable desktop/tablet in desktop layout still behaves click-only.
  onDotsTouchStart(event: TouchEvent): void {
    this.handleDotsTouch(event);
  }

  onDotsTouchMove(event: TouchEvent): void {
    this.handleDotsTouch(event);
  }

  onDotsTouchEnd(): void {
    // No-op: the last touchmove already landed the carousel on the right card.
  }

  private handleDotsTouch(event: TouchEvent): void {
    if (!this.isMobileViewport()) return; // desktop stays click-only, per spec
    const touch = event.touches[0];
    if (!touch) return;
    const hit = document.elementFromPoint(touch.clientX, touch.clientY);
    const dot = hit?.closest<HTMLElement>('.ns-dot');
    if (!dot || !dot.parentElement) return;
    const index = Array.from(dot.parentElement.children).indexOf(dot);
    if (index === -1 || index === this.activeSlide) return;
    event.preventDefault(); // this is a deliberate scrub — don't also let the page try to scroll
    this.goToSlide(index);
  }

  private isMobileViewport(): boolean {
    return (
      isPlatformBrowser(this.platform) &&
      window.innerWidth <= IssuerNextStepsPanelComponent.MOBILE_BREAKPOINT_PX
    );
  }

  onCta(nudge: IssuerNudge): void {
    switch (nudge.action) {
      case 'navigate':
        this.goTo(nudge.route);
        break;
      case 'external':
        this.openExternal(nudge.externalUrl);
        break;
      case 'announce':
        this.openAnnouncementDialog();
        break;
      case 'upload':
        this.openUploadDialog();
        break;
      case 'none':
        break; // dividend teaser — deliberately inert
    }
  }

  /**
   * In-app navigation for the card CTAs. Both current targets
   * (`/postfundraising/shareholders`, `/raise/raise-fund-form`) are declared in
   * app.routing.ts as `roles: [Role.IssuerRep]` behind AuthGuard — the same
   * audience this panel is gated on, so the guard always passes for anyone who
   * can see the card.
   */
  goTo(route?: string): void {
    if (!route) return;
    void this.router.navigateByUrl(route);
  }

  /**
   * Cards 1 & 2 act *on* the Shareholders Dashboard, so their CTAs take the issuer
   * there first and open the dialog on arrival, rather than floating it over
   * whatever page they happened to be on. The point is where they're left afterwards:
   * when the dialog closes they're on the page that lists announcements and
   * documents, so they can see the thing they just created.
   *
   * Two cases are handled deliberately:
   * - Already on the dashboard → skip the navigation entirely and open at once,
   *   so re-posting from that page doesn't trigger a pointless self-navigation.
   * - Navigation refused (guard, redirect, or a resolver rejecting) →
   *   `navigateByUrl` resolves `false`, and we do NOT open. A dialog acting on a
   *   page the issuer never reached is worse than nothing happening.
   */
  private openOnShareholdersDashboard(openDialog: () => void): void {
    const target = IssuerNextStepsPanelComponent.SHAREHOLDERS_ROUTE;
    // `router.url` carries any query string / fragment — compare the path alone.
    if (this.router.url.split(/[?#]/)[0] === target) {
      openDialog();
      return;
    }

    void this.router.navigateByUrl(target).then((navigated) => {
      if (navigated) openDialog();
    });
  }

  /**
   * Card 1 — reuses the Shareholders Dashboard's own Post Announcement modal, which
   * the ticket's screenshot identifies as "current practice". Reused rather than
   * reimplemented so this panel can't drift from that page: the modal owns its
   * validation, its POST, its success popup, and — importantly for the ticket's
   * "failed post → dialog stays open, nothing lost" AC — its error handling, since
   * it reports errors without closing itself.
   *
   * Note we deliberately do nothing with a successful result. The card must survive
   * posting ("the card is still there ... that's the point"), so there's no dismissal
   * or badge mutation here.
   *
   * Routes to the Shareholders Dashboard first — see openOnShareholdersDashboard().
   * The companyId is checked *before* navigating, so a missing company doesn't drag
   * the issuer to another page only to fail there.
   */
  private openAnnouncementDialog(): void {
    if (this.companyId == null) {
      this.toast.error(this.translate.instant('nextSteps.toast.companyMissing'));
      return;
    }

    this.openOnShareholdersDashboard(() => {
      const modalRef = this.modalService.open(PostNewAnnouncementComponent, {
        centered: true,
        size: 'md',
        windowClass: 'modal-550',
        backdropClass: 'backdrop-popup',
      });
      modalRef.componentInstance.companyId = this.companyId;

      // Dismissal (backdrop / Cancel) rejects the result promise. Swallow it —
      // an unhandled rejection here would surface as a console error on every cancel.
      modalRef.result.catch(() => {});
    });
  }

  /**
   * Card 2 — reuses the Shareholders Dashboard's own Upload Other Documents modal
   * (the ticket's second screenshot). The modal validates the file *before* any
   * upload via FileValidationService, which is what satisfies the "over 30 MB
   * rejected before uploading" and "disallowed type rejected" ACs — both surface
   * inside the still-open dialog.
   *
   * Unlike the announcement modal this one does NOT finish the job: it uploads the
   * file to blob storage, closes, and hands the caller an `attachmentInfo` to
   * register against the company. So the metadata POST below is ours to do.
   *
   * KNOWN AC GAP: "failed upload → dialog stays open" holds for file validation and
   * for the blob upload (both inside the modal), but NOT for this final metadata
   * POST — by then the modal has already closed, so a server error here can only be
   * surfaced as an error message, not a re-opened dialog. The live Shareholders
   * Dashboard behaves identically, so this matches "same as current practice";
   * closing the gap properly means changing the shared modal to own its own POST.
   *
   * Routes to the Shareholders Dashboard first — see openOnShareholdersDashboard().
   *
   * TODO(FRC-686): this mirrors ticker-code.component.ts#openUploadModal. Worth
   * extracting into a shared service once both callers can be retested together.
   */
  private openUploadDialog(): void {
    this.openOnShareholdersDashboard(() => {
      const modalRef = this.modalService.open(GeneralDocumentUploadComponent, {
        centered: true,
        size: 'md',
        windowClass: 'modal-550',
        backdropClass: 'backdrop-popup',
      });

      // Explicit blank document + limit, mirroring the dashboard's call. The component
      // defaults would cover it, but being explicit keeps the two call sites diffable.
      modalRef.componentInstance.document = {
        fileName: '',
        file: null,
        attachmentInfo: null,
        url: '',
      };
      modalRef.componentInstance.fileSizeLimit = IssuerNextStepsPanelComponent.MAX_UPLOAD_BYTES;

      modalRef.result
        .then((result) => {
          if (result?.attachmentInfo) this.saveUploadedDocument(result);
        })
        // Dismissal (backdrop / Cancel) rejects — nothing to do.
        .catch(() => {});
    });
  }

  /** Register an already-uploaded document against the company. */
  private saveUploadedDocument(result: {
    fileName?: string;
    audience?: AttachmentAudience;
    attachmentInfo: { id: string; token: string; encryptedAttachmentInfo: string };
  }): void {
    // Match the same rule the backend uses to resolve "the" general/other document
    // type (CanHaveMultiple + the issuer's chosen Audience) - mirrors the dashboard's
    // fix. The read path filters documents by AttachmentType.Audience, so the type has
    // to carry the audience the issuer picked in the modal, not always All. No more
    // `|| this.attachmentTypes[0]` fallback: that grabbed whatever type happened to
    // sort first, which could be a single-instance type the company already has a
    // record for, causing a false "already exists" error on the company's very
    // first general-document upload.
    const audience: AttachmentAudience = result.audience;
    const attachmentType = this.attachmentTypes.find(
      (t) => t.canHaveMultiple && t.audience === audience,
    );
    if (!attachmentType) {
      this.errorService.showError(
        'Unable to resolve the general document upload type. Please try again later.',
      );
      return;
    }

    const formData = new FormData();
    formData.append('type', attachmentType.id.toString());
    formData.append('status', '1');
    formData.append('audience', audience.toString());
    formData.append('documentName', result.fileName || '');
    formData.append('AttachmentId', result.attachmentInfo.id);
    formData.append('AttachmentInfo.Token', result.attachmentInfo.token);
    formData.append(
      'AttachmentInfo.EncryptedAttachmentInfo',
      result.attachmentInfo.encryptedAttachmentInfo,
    );

    this.blockUiService.open();
    this.businessesService
      .saveTickerDocument(formData)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        // Card intentionally stays put — same reasoning as the announcement flow.
        next: () => this.toast.success(this.translate.instant('nextSteps.toast.documentUploaded')),
        error: (err) => this.errorService.showError(err),
      });
  }

  /**
   * Open an ops-hosted form in a new tab, keeping the issuer's place on the site.
   *
   * `noopener` is deliberate: it stops the opened page reaching back via
   * `window.opener`, and it's also why we can't detect a blocked popup by testing
   * the return value — with `noopener` browsers legitimately return `null` on
   * success too. So we surface the "opening in a new tab" toast unconditionally
   * rather than trying to branch on it; if a popup blocker does swallow the tab,
   * the toast is the issuer's cue that something was meant to happen.
   *
   * `noreferrer` is paired with it to stop the issuer's current URL leaking into the
   * form's referrer — that URL can itself be sensitive (a campaign or dashboard path)
   * and these forms are third-party hosted. It also covers reverse-tabnabbing on
   * older browsers that honour `noreferrer` but not `noopener`.
   */
  private openExternal(url?: string): void {
    if (!isPlatformBrowser(this.platform) || !url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    this.toast.info(this.translate.instant('nextSteps.toast.formOpened'));
  }
}
